/**
 * AI Discovery Extractor
 * Extract discovery question answers from meeting notes and apply them to CIs
 * Connects post-meeting AI analysis with the discovery context system
 */

import React, { useState } from 'react';
import { Sparkles, Loader2, CheckCircle, AlertCircle, FileText } from 'lucide-react';
import { projectId } from '../utils/supabase/info';
import { getAuthHeadersWithJson } from '../utils/apiHeaders';
import { toast } from 'sonner';

interface AIDiscoveryExtractorProps {
  companyId: string;
  themeId: string;
  themeName: string;
  ciId: string;
  ciName: string;
  accessToken: string;
  onSuccess: () => void;
}

export function AIDiscoveryExtractor({
  companyId,
  themeId,
  themeName,
  ciId,
  ciName,
  accessToken,
  onSuccess,
}: AIDiscoveryExtractorProps) {
  const [meetingNotes, setMeetingNotes] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [extractedAnswers, setExtractedAnswers] = useState<any[]>([]);
  const [summary, setSummary] = useState('');
  const [unansweredQuestions, setUnansweredQuestions] = useState<string[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [applying, setApplying] = useState(false);

  const handleExtract = async () => {
    if (!meetingNotes.trim()) {
      toast.error('Please enter meeting notes or transcript');
      return;
    }

    try {
      setExtracting(true);
      toast.info('AI is analyzing meeting notes...');

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consultant/ai/extract-discovery-answers`,
        {
          method: 'POST',
          headers: getAuthHeadersWithJson(accessToken),
          body: JSON.stringify({
            companyId,
            themeId,
            ciId,
            ciName,
            meetingNotes,
            questionsToAnswer: [], // Will use general extraction
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to extract answers');
      }

      const data = await response.json();
      setExtractedAnswers(data.extractedAnswers || []);
      setSummary(data.summary || '');
      setUnansweredQuestions(data.unansweredQuestions || []);
      setShowResults(true);
      
      toast.success(`AI extracted ${data.extractedAnswers?.length || 0} answers`);
    } catch (err: any) {
      console.error('Error extracting answers:', err);
      toast.error(`Failed to extract answers: ${err.message}`);
    } finally {
      setExtracting(false);
    }
  };

  const handleApplyAnswers = async () => {
    if (extractedAnswers.length === 0) {
      toast.error('No answers to apply');
      return;
    }

    try {
      setApplying(true);
      toast.info('Applying answers to discovery context...');

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consultant/ai/apply-extracted-answers`,
        {
          method: 'POST',
          headers: getAuthHeadersWithJson(accessToken),
          body: JSON.stringify({
            companyId,
            themeId,
            ciId,
            extractedAnswers,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to apply answers');
      }

      const data = await response.json();
      
      toast.success(`Applied ${data.appliedCount} answers. Build readiness now ${data.buildReadiness}%`);
      
      // Reset state
      setMeetingNotes('');
      setExtractedAnswers([]);
      setSummary('');
      setShowResults(false);
      
      // Trigger refresh
      onSuccess();
    } catch (err: any) {
      console.error('Error applying answers:', err);
      toast.error(`Failed to apply answers: ${err.message}`);
    } finally {
      setApplying(false);
    }
  };

  const getConfidenceColor = (confidence: string) => {
    switch (confidence) {
      case 'high':
        return 'bg-green-100 text-green-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex items-start gap-3">
        <Sparkles className="size-5 text-purple-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <h4 className="text-purple-900">AI Discovery Extraction</h4>
          <p className="text-sm text-purple-700 mt-1">
            Paste meeting notes or transcript, and AI will automatically extract answers to discovery questions for <strong>{ciName}</strong>.
          </p>
        </div>
      </div>

      {!showResults ? (
        // Input Phase
        <div className="space-y-3">
          <div>
            <label className="block text-sm text-gray-700 mb-2">
              Meeting Notes or Transcript
            </label>
            <textarea
              value={meetingNotes}
              onChange={(e) => setMeetingNotes(e.target.value)}
              placeholder="Paste your meeting notes, transcript, or discussion summary here...

Example:
- We discussed the Hardware Request workflow
- Sarah mentioned they need manager approval for items over $500
- The team wants a 3-day SLA for standard hardware requests
- Integration with their asset management system is critical"
              rows={12}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
            />
            <div className="text-xs text-gray-600 mt-1">
              {meetingNotes.length} characters
            </div>
          </div>

          <button
            onClick={handleExtract}
            disabled={extracting || !meetingNotes.trim()}
            className="w-full px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {extracting ? (
              <>
                <Loader2 className="size-5 animate-spin" />
                AI is Analyzing...
              </>
            ) : (
              <>
                <Sparkles className="size-5" />
                Extract Answers with AI
              </>
            )}
          </button>
        </div>
      ) : (
        // Results Phase
        <div className="space-y-4">
          {/* Summary */}
          {summary && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <FileText className="size-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-blue-900">Meeting Summary</div>
                  <div className="text-sm text-blue-700 mt-1">{summary}</div>
                </div>
              </div>
            </div>
          )}

          {/* Extracted Answers */}
          <div>
            <h4 className="text-gray-900 mb-3">Extracted Answers ({extractedAnswers.length})</h4>
            
            {extractedAnswers.length === 0 ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                <AlertCircle className="size-8 text-yellow-600 mx-auto mb-2" />
                <div className="text-yellow-900">
                  No clear answers were found in the meeting notes
                </div>
                <div className="text-sm text-yellow-700 mt-1">
                  Try providing more detailed notes or specific discussion points
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {extractedAnswers.map((answer, idx) => (
                  <div
                    key={idx}
                    className="border border-gray-200 rounded-lg p-4 bg-white space-y-2"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2 flex-1">
                        <CheckCircle className="size-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <div className="text-sm text-gray-700 mb-1">{answer.question}</div>
                          <div className="text-gray-900">{answer.answer}</div>
                        </div>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded ${getConfidenceColor(answer.confidence)}`}>
                        {answer.confidence}
                      </span>
                    </div>
                    
                    {answer.sourceQuote && (
                      <div className="ml-7 bg-gray-50 border-l-2 border-gray-300 pl-3 py-2 text-sm text-gray-700 italic">
                        "{answer.sourceQuote}"
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Unanswered Questions */}
          {unansweredQuestions.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="size-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <div className="text-yellow-900 mb-1">Not Discussed in Meeting</div>
                  <ul className="text-sm text-yellow-700 space-y-1">
                    {unansweredQuestions.map((q, idx) => (
                      <li key={idx}>• {q}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2">
            <button
              onClick={handleApplyAnswers}
              disabled={applying || extractedAnswers.length === 0}
              className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {applying ? (
                <>
                  <Loader2 className="size-5 animate-spin" />
                  Applying...
                </>
              ) : (
                <>
                  <CheckCircle className="size-5" />
                  Apply {extractedAnswers.length} Answer{extractedAnswers.length !== 1 ? 's' : ''} to Discovery Context
                </>
              )}
            </button>
            <button
              onClick={() => {
                setShowResults(false);
                setExtractedAnswers([]);
                setSummary('');
              }}
              className="px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Start Over
            </button>
          </div>
        </div>
      )}
    </div>
  );
}